import { useState, useEffect, useMemo } from 'react';
import { useOne, useList, useCustom, useCustomMutation, useGo } from '@refinedev/core';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Info,
  MessageSquare,
  History,
  CreditCard,
  DollarSign,
  UserCheck,
  ShoppingCart,
  Calendar,
  User,
  Package,
  Percent,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FormHeader } from '@/components/forms';
import { EntityAnalyticsCard, EntityAnalyticsGrid, TwoColumnLayout } from '@/components/shared';
import {
  OrderUserSection,
  OrderItemsSection,
  OrderPaymentsSection,
} from './components/OrderSections';
import { OrderHistory } from './components/OrderHistory';
import { OrderNotes } from './components/OrderNotes';
import { OrderDialogPanel } from './components/OrderDialogPanel';
import { OrderPaymentsTab } from './components/OrderPaymentsTab';
import {
  getTargetTypeLabel,
  type Order,
  type AdminUser,
  type OrderStatusConfig,
  type OrderStatusTransition,
  type OrderUser,
} from './types';

function formatCurrency(value: string | number, currency?: string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
  return currency ? `${formatted} ${currency}` : formatted;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function getUserName(user: OrderUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (user.username) {
    return `@${user.username}`;
  }
  return `Пользователь #${user.id}`;
}

type ResolvedTransition = OrderStatusTransition & { toStatus?: OrderStatusConfig };

// ---- Status control (selector + confirmation dialog) ----

function OrderStatusControl({
  order,
  resolvedTransitions,
  onStatusChange,
  isPending,
}: {
  order: Order;
  resolvedTransitions: ResolvedTransition[];
  onStatusChange: (statusId: number, note?: string) => void;
  isPending: boolean;
}) {
  const [pendingTransition, setPendingTransition] = useState<ResolvedTransition | null>(null);
  const [note, setNote] = useState('');

  const currentColor = order.statusConfig?.color ?? undefined;
  const currentValue = String(order.statusConfigId);

  const handleSelect = (value: string) => {
    if (value === currentValue) { return; }
    const transition = resolvedTransitions.find((t) => String(t.toStatusId) === value);
    if (!transition) { return; }
    if (transition.requireNote || transition.requireConfirmation) {
      setNote('');
      setPendingTransition(transition);
      return;
    }
    onStatusChange(transition.toStatusId);
  };

  const confirmDisabled =
    isPending || (pendingTransition?.requireNote === true && note.trim().length === 0);

  return (
    <>
      <Select value={currentValue} onValueChange={handleSelect} disabled={isPending}>
        <SelectTrigger
          className="h-9 w-[200px] text-sm font-medium"
          style={{ borderColor: currentColor, color: currentColor }}
        >
          {isPending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Обновление…
            </span>
          ) : (
            <SelectValue />
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={currentValue} disabled>
            {order.statusConfig?.name ?? 'Текущий статус'}
          </SelectItem>
          {resolvedTransitions.map((t) => (
            <SelectItem key={t.id} value={String(t.toStatusId)}>
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: t.toStatus?.color ?? 'var(--muted)' }}
                />
                {t.toStatus?.name ?? `#${t.toStatusId}`}
              </span>
            </SelectItem>
          ))}
          {resolvedTransitions.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
              Переходы недоступны
            </div>
          )}
        </SelectContent>
      </Select>

      <Dialog
        open={pendingTransition !== null}
        onOpenChange={(open) => {
          if (!open) { setPendingTransition(null); }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Перевести в «{pendingTransition?.toStatus?.name ?? ''}»?
            </DialogTitle>
            <DialogDescription>
              {pendingTransition?.requireNote
                ? 'Для этого перехода требуется заметка.'
                : 'Подтвердите смену статуса заказа.'}
            </DialogDescription>
          </DialogHeader>

          {pendingTransition?.requireNote && (
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Комментарий к смене статуса…"
              rows={3}
              autoFocus
            />
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingTransition(null)}
              disabled={isPending}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                if (!pendingTransition) { return; }
                onStatusChange(
                  pendingTransition.toStatusId,
                  pendingTransition.requireNote ? note.trim() : undefined,
                );
                setPendingTransition(null);
              }}
              disabled={confirmDisabled}
            >
              {isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Подтвердить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---- Manager control ----

function OrderManagerControl({
  order,
  admins,
  onAssignChange,
  isPending,
}: {
  order: Order;
  admins: AdminUser[];
  onAssignChange: (value: string) => void;
  isPending: boolean;
}) {
  const selectedAdminId = order.assignedAdminId ? String(order.assignedAdminId) : 'none';

  return (
    <Select value={selectedAdminId} onValueChange={onAssignChange} disabled={isPending}>
      <SelectTrigger className="h-9 w-[190px] text-sm">
        <span className="flex items-center gap-1.5 truncate">
          <UserCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <SelectValue placeholder="Не назначен" />
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Не назначен</SelectItem>
        {admins.map((a) => (
          <SelectItem key={a.id} value={String(a.id)}>
            {a.username}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---- Order info popover ----

function OrderInfoPopover({ order }: { order: Order }) {
  const go = useGo();
  const discountTotal = parseFloat(order.discountTotal ?? '0');
  const hasDiscount = discountTotal > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Информация о заказе">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Package className="h-3.5 w-3.5" />
            Заказ #{order.id}
          </div>

          <button
            type="button"
            className="-mx-1.5 flex w-full cursor-pointer items-center gap-2 rounded-md p-1.5 text-left transition-colors hover:bg-muted/50"
            onClick={() => go({ to: `/users/show/${order.userId}` })}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <User className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{getUserName(order.user)}</p>
              {order.user.username && (
                <p className="text-xs text-muted-foreground">@{order.user.username}</p>
              )}
            </div>
          </button>

          <Separator />

          <div className="space-y-1">
            {order.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-xs">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
                    {getTargetTypeLabel(item.targetType)}
                  </Badge>
                  <span className="truncate">{item.target?.name ?? `#${item.targetId}`}</span>
                  {item.quantity > 1 && (
                    <span className="shrink-0 text-muted-foreground">x{item.quantity}</span>
                  )}
                </div>
                <span className="ml-2 shrink-0 font-mono text-muted-foreground">
                  {formatCurrency(item.total)}
                </span>
              </div>
            ))}
          </div>

          <Separator />

          <div className="space-y-1">
            {hasDiscount && (
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Percent className="h-3 w-3" />
                  Скидка
                </span>
                <span className="font-mono text-green-600">
                  -{formatCurrency(discountTotal, order.currency)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm font-medium">
              <span>Итого</span>
              <span className="font-mono">{formatCurrency(order.total, order.currency)}</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function OrdersShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'overview');

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Load order
  const orderResult = useOne<Order>({
    resource: 'orders',
    id: Number(id),
  });

  const order = orderResult.result;
  const isLoading = orderResult.query?.isLoading ?? false;
  const refetchOrder = orderResult.query?.refetch;

  // Load admin users
  const adminsResult = useList<AdminUser>({
    resource: 'admin-users',
    pagination: { mode: 'off' },
  });
  const adminsData = adminsResult.result?.data;
  const admins = useMemo(() => adminsData ?? [], [adminsData]);
  const adminMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const admin of admins) {
      map.set(admin.id, admin.username);
    }
    return map;
  }, [admins]);

  // Load all statuses
  const statusesResult = useList<OrderStatusConfig>({
    resource: 'order-statuses',
    pagination: { mode: 'off' },
  });
  const allStatuses = useMemo(
    () => statusesResult.result?.data ?? [],
    [statusesResult.result?.data],
  );

  // Available transitions for current status
  const transitionsResult = useCustom<OrderStatusTransition[]>({
    url: order ? `/order-statuses/${order.statusConfigId}/transitions` : '',
    method: 'get',
    queryOptions: { enabled: Boolean(order) },
  });
  const transitions = useMemo(
    () => transitionsResult.query?.data?.data ?? [],
    [transitionsResult.query?.data?.data],
  );

  const statusChangeMutation = useCustomMutation();
  const assignMutation = useCustomMutation();

  const resolvedTransitions = useMemo<ResolvedTransition[]>(
    () =>
      transitions.map((t) => ({
        ...t,
        toStatus: allStatuses.find((s) => s.id === t.toStatusId),
      })),
    [transitions, allStatuses],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Заказ не найден</p>
        <Button variant="link" onClick={() => go({ to: '/orders' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const handleStatusChange = (statusId: number, note?: string) => {
    statusChangeMutation.mutate(
      {
        url: `/orders/${order.id}/crm-status`,
        method: 'patch',
        values: { statusId, note, confirmed: true },
      },
      {
        onSuccess: () => {
          refetchOrder?.();
          transitionsResult.query?.refetch?.();
        },
      },
    );
  };

  const handleAssignChange = (value: string) => {
    const adminId = value === 'none' ? null : Number(value);
    assignMutation.mutate(
      {
        url: `/orders/${order.id}/assign`,
        method: 'patch',
        values: { adminId },
      },
      {
        onSuccess: () => refetchOrder?.(),
      },
    );
  };

  return (
    <div className="space-y-3">
      <FormHeader
        title={`Заказ #${order.id}`}
        description={`${getUserName(order.user)} • ${formatDate(order.createdAt)}`}
        icon={<ShoppingCart className="h-5 w-5" />}
        sticky
        actions={
          <>
            <OrderStatusControl
              order={order}
              resolvedTransitions={resolvedTransitions}
              onStatusChange={handleStatusChange}
              isPending={statusChangeMutation.mutation.isPending}
            />
            <OrderManagerControl
              order={order}
              admins={admins}
              onAssignChange={handleAssignChange}
              isPending={assignMutation.mutation.isPending}
            />
            <OrderInfoPopover order={order} />
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="grid w-full grid-cols-5 lg:inline-flex lg:w-auto">
          <TabsTrigger value="overview" className="flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Обзор</span>
          </TabsTrigger>
          <TabsTrigger value="crm" className="flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">CRM</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Платежи</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Заметки</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">История</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" forceMount className="data-[state=inactive]:hidden">
          <OverviewTab order={order} adminMap={adminMap} onRefetch={() => refetchOrder?.()} />
        </TabsContent>

        <TabsContent value="crm" forceMount className="data-[state=inactive]:hidden">
          <OrderDialogPanel orderId={order.id} user={order.user} />
        </TabsContent>

        <TabsContent value="payments" forceMount className="data-[state=inactive]:hidden">
          <OrderPaymentsTab order={order} onRefetch={() => refetchOrder?.()} />
        </TabsContent>

        <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden">
          <OrderNotes orderId={order.id} />
        </TabsContent>

        <TabsContent value="history" forceMount className="data-[state=inactive]:hidden">
          <Card>
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-sm">История заказа</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <OrderHistory orderId={order.id} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Overview Tab ----

function OverviewTab({
  order,
  adminMap,
  onRefetch,
}: {
  order: Order;
  adminMap: Map<number, string>;
  onRefetch: () => void;
}) {
  const assignedName = order.assignedAdminId
    ? adminMap.get(order.assignedAdminId) ?? `Админ #${order.assignedAdminId}`
    : 'Не назначен';

  // OrderSections expect InferSelectModel<typeof order> but cast internally.
  const orderRecord = order as Parameters<typeof OrderUserSection>[0]['record'];

  return (
    <div className="space-y-3">
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Сумма"
          value={formatCurrency(order.total, order.currency)}
          icon={<DollarSign className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Статус"
          value={order.statusConfig?.name ?? 'Неизвестно'}
          icon={<Info className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Назначен"
          value={assignedName}
          icon={<UserCheck className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Создан"
          value={formatDate(order.createdAt)}
          icon={<Calendar className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      <TwoColumnLayout
        gap="sm"
        main={
          <div className="space-y-3">
            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm">Товары</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <OrderItemsSection record={orderRecord} onRefresh={onRefetch} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm">Платежи</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <OrderPaymentsSection record={orderRecord} />
              </CardContent>
            </Card>
          </div>
        }
        sidebar={
          <Card>
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-sm">Пользователь</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <OrderUserSection record={orderRecord} />
            </CardContent>
          </Card>
        }
      />
    </div>
  );
}
